// Ping (admins; "inbox" in code): notifications from Azeem's sites, apps and scripts, plus MonoLog's own alerts.
// A sidebar of views and sources (chips on mobile), a search, and a feed grouped by day. Opening
// an item marks it read and shows the whole message, its data and its link; items can be pinned,
// archived (swipe left on mobile) or marked unread. The feed polls every 10 seconds while it's on
// screen and new items glow briefly; the tab badge and the page title carry the unread count.
// Keyboard: j/k move, Enter opens, e archives, p pins, u toggles read, / searches, Esc closes.
import type { InboxCounts, InboxFilter, InboxItem, InboxSourceCount, InboxView, Level } from "../src/inbox-types";
import { markdown, preview } from "./md";
import { notifyEndpoint } from "../src/host";

interface Opts {
  el: HTMLElement;
  mobile: boolean;
  current: () => boolean;
  /** Open (and scroll to) this notification, from a push or a /inbox/?n= link. */
  openId?: string | null;
  /** Settings → Ping, for "Add a source". */
  onSettings: () => void;
}

const POLL_MS = 10_000;
const BADGE_MS = 30_000;
const PAGE = 50;

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

// ---------- icons (1.6px strokes, 20px box) ----------
const svg = (d: string, cls = "") => `<svg class="ic20 ${cls}" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const I = {
  inbox: svg('<path d="M3 11.5 5.2 4.6A1.5 1.5 0 0 1 6.6 3.5h6.8a1.5 1.5 0 0 1 1.4 1.1L17 11.5"/><path d="M3 11.5V15a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 17 15v-3.5h-3.6l-1 2h-4.8l-1-2Z"/>'),
  dot: svg('<circle cx="10" cy="10" r="3.2" fill="currentColor" stroke="none"/><circle cx="10" cy="10" r="6.5"/>'),
  alert: svg('<path d="M10 3.2 17.4 16H2.6Z"/><path d="M10 8.2v3.6"/><circle cx="10" cy="13.9" r=".5" fill="currentColor"/>'),
  pin: svg('<path d="M12.6 2.9 17 7.4l-2.2.8-3 3 .4 3.5-1.4 1.4-3.2-3.2-4 4-.6-.6 4-4L3.8 9.1l1.4-1.4 3.5.4 3-3Z"/>'),
  archive: svg('<rect x="2.8" y="3.6" width="14.4" height="3.6" rx="1"/><path d="M4.2 7.2V15a1.4 1.4 0 0 0 1.4 1.4h8.8a1.4 1.4 0 0 0 1.4-1.4V7.2"/><path d="M8 10.4h4"/>'),
  check: svg('<path d="m4.4 10.4 3.5 3.4 7.7-7.6"/>'),
  checks: svg('<path d="m2.6 10.6 3.3 3.2 7.2-7.2"/><path d="m10.4 13.4.4.4 7.2-7.2"/>'),
  search: svg('<circle cx="8.8" cy="8.8" r="5.4"/><path d="m13 13 4 4"/>'),
  out: svg('<path d="M8.4 4.4H5.2a1.4 1.4 0 0 0-1.4 1.4v9a1.4 1.4 0 0 0 1.4 1.4h9a1.4 1.4 0 0 0 1.4-1.4v-3.2"/><path d="M11.6 3.6h4.8v4.8"/><path d="m16.4 3.6-7.2 7.2"/>'),
  unread: svg('<circle cx="10" cy="10" r="3.4" fill="currentColor" stroke="none"/>'),
  undo: svg('<path d="M6.4 8.4H13a3.6 3.6 0 0 1 0 7.2H9"/><path d="m8.8 5.6-2.8 2.8 2.8 2.8"/>'),
  gear: svg('<circle cx="10" cy="10" r="2.4"/><path d="M10 2.8v1.6M10 15.6v1.6M2.8 10h1.6M15.6 10h1.6M4.9 4.9 6 6M14 14l1.1 1.1M4.9 15.1 6 14M14 6l1.1-1.1"/>'),
  x: svg('<path d="m5.5 5.5 9 9M14.5 5.5l-9 9"/>'),
};

const LEVEL_LABEL: Record<Level, string> = { info: "Info", success: "Success", warning: "Warning", critical: "Critical" };
const VIEWS: { key: InboxFilter; label: string; icon: string }[] = [
  { key: "all", label: "All", icon: I.inbox },
  { key: "unread", label: "Unread", icon: I.dot },
  { key: "critical", label: "Critical", icon: I.alert },
  { key: "pinned", label: "Pinned", icon: I.pin },
  { key: "archived", label: "Archived", icon: I.archive },
];
const VIEW_TITLE: Record<InboxFilter, string> = { all: "Ping", unread: "Unread", critical: "Critical", pinned: "Pinned", archived: "Archived" };

// ---------- state (kept while the person moves around the app) ----------

const st = {
  filter: "all" as InboxFilter,
  source: "",
  q: "",
  items: [] as InboxItem[],
  pinned: [] as InboxItem[],
  next: null as string | null,
  counts: null as InboxCounts | null,
  sources: [] as InboxSourceCount[],
  open: new Set<string>(),
  focus: null as string | null,
  /** id → "last_at:count", to spot what's new on each poll. */
  seen: new Map<string, string>(),
  fresh: new Set<string>(),
  loaded: false,
  error: null as string | null,
  /** id → what its action button (Fix it) last did. */
  actionMsg: new Map<string, { text: string; error: boolean }>(),
};
let opts: Opts | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let gen = 0;

// ---------- API ----------

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (res.status === 401 && (out as { logins?: boolean }).logins) location.href = "/login";
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

const query = (extra: Record<string, string> = {}) =>
  new URLSearchParams({ filter: st.filter, ...(st.source ? { source: st.source } : {}), ...(st.q.trim() ? { q: st.q.trim() } : {}), ...extra }).toString();

// ---------- badge and title ----------

let lastCounts: InboxCounts | null = null;
let badgeTimer: ReturnType<typeof setInterval> | null = null;

/** The unread count on the Ping tabs and in the page title. */
export function setInboxBadge(c: InboxCounts | null): void {
  lastCounts = c;
  const n = c?.unread ?? 0;
  for (const b of document.querySelectorAll<HTMLElement>("[data-inbox-badge]")) {
    b.hidden = !n;
    b.textContent = n > 99 ? "99+" : String(n);
    b.classList.toggle("crit", !!c?.critical);
  }
  const base = document.title.replace(/^\(\d+\+?\)\s*/, "");
  document.title = n ? `(${n > 99 ? "99+" : n}) ${base}` : base;
}

async function refreshBadge(): Promise<void> {
  try {
    setInboxBadge(await call<InboxCounts>("GET", "inbox/count"));
  } catch {
    // The badge waits for the next tick.
  }
}

/** Admins: keep the badge current everywhere in the app (every 30 s while visible). */
export function startInboxBadge(): void {
  if (badgeTimer) return;
  void refreshBadge();
  badgeTimer = setInterval(() => !document.hidden && void refreshBadge(), BADGE_MS);
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      void refreshBadge();
      if (opts?.current()) void poll();
    }
  });
}

// ---------- formatting ----------

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const dayStart = (ms: number) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const clock = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

function dayLabel(ms: number, now = Date.now()): string {
  const days = Math.round((dayStart(now) - dayStart(ms)) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  const d = new Date(ms);
  if (days < 7) return DOW[d.getDay()]!;
  return `${d.getDate()} ${MON[d.getMonth()]}${d.getFullYear() === new Date(now).getFullYear() ? "" : ` ${d.getFullYear()}`}`;
}

function when(ms: number, now = Date.now()): string {
  const s = Math.max(0, (now - ms) / 1000);
  if (dayStart(ms) !== dayStart(now)) return clock(ms);
  if (s < 45) return "now";
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

const fullDate = (ms: number) => new Date(ms).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const plural = (n: number, w: string) => `${n.toLocaleString()} ${w}${n === 1 ? "" : "s"}`;

/** A source's avatar: its emoji on a tint of its colour, or its initial on the colour. MonoLog gets its wordmark M. */
export function avatar(s: { name: string; icon: string; color: string; system?: boolean }, size: "sm" | "md" = "md", level?: Level): string {
  const ring = level === "critical" || level === "warning" ? `<i class="ibx-ring lv-${level}" aria-hidden="true"></i>` : "";
  if (s.system) return `<span class="ibx-ava ${size} sys" aria-hidden="true">M${ring}</span>`;
  const c = /^#[0-9a-f]{6}$/i.test(s.color) ? s.color : "#2f5bd3";
  if (s.icon) return `<span class="ibx-ava ${size} emo" style="--c:${c}" aria-hidden="true"><b>${esc(s.icon)}</b>${ring}</span>`;
  return `<span class="ibx-ava ${size} ini" style="--c:${c}" aria-hidden="true">${esc((s.name.match(/[\p{L}\p{N}]/u)?.[0] ?? "?").toUpperCase())}${ring}</span>`;
}

const sig = (i: InboxItem) => `${i.last_at}:${i.count}:${i.read_at ? 1 : 0}:${i.pinned ? 1 : 0}`;

// ---------- rendering ----------

function itemHtml(i: InboxItem, now: number): string {
  const open = st.open.has(i.id);
  const unread = !i.read_at;
  const name = i.label || i.source.name;
  const lvl = i.level === "critical" || i.level === "warning" ? `<span class="ibx-lvl lv-${i.level}">${LEVEL_LABEL[i.level]}</span>` : "";
  const count = i.count > 1 ? `<span class="ibx-count" title="${plural(i.count, "time")}">×${i.count}</span>` : "";
  const prev = preview(i.message);
  const cls = ["ibx-item", `lv-${i.level}`, unread ? "unread" : "", open ? "open" : "", st.fresh.has(i.id) ? "fresh" : "", st.focus === i.id ? "focus" : ""].filter(Boolean).join(" ");
  const detail = open ? detailHtml(i) : "";
  return `<article class="${cls}" data-ibx-id="${esc(i.id)}">
  <div class="ibx-swipe" aria-hidden="true"><span class="sw-l">${unread ? I.check : I.unread}<b>${unread ? "Read" : "Unread"}</b></span><span class="sw-r">${I.archive}<b>${i.archived_at ? "Restore" : "Archive"}</b></span></div>
  <div class="ibx-row" role="button" tabindex="0" aria-expanded="${open}" data-ibx-toggle>
    ${avatar(i.source, "md", i.level)}
    <div class="ibx-main">
      <div class="ibx-l1"><span class="ibx-title">${esc(i.title)}</span>${count}${lvl}${i.pinned ? `<span class="ibx-pinned" title="Pinned">${I.pin}</span>` : ""}</div>
      <div class="ibx-l2"><span class="ibx-src">${esc(name)}</span>${prev ? `<span class="ibx-prev">${esc(prev.slice(0, 220))}</span>` : ""}</div>
    </div>
    <div class="ibx-side-meta"><time datetime="${new Date(i.last_at).toISOString()}" title="${esc(fullDate(i.last_at))}">${when(i.last_at, now)}</time><span class="ibx-udot" aria-label="${unread ? "Unread" : ""}"></span></div>
    <div class="ibx-hover" role="group" aria-label="Actions">
      <button class="ibx-icon-btn" data-ibx-act="read" title="${unread ? "Mark read (u)" : "Mark unread (u)"}" aria-label="${unread ? "Mark read" : "Mark unread"}">${unread ? I.check : I.unread}</button>
      <button class="ibx-icon-btn${i.pinned ? " on" : ""}" data-ibx-act="pin" title="${i.pinned ? "Unpin" : "Pin"} (p)" aria-label="${i.pinned ? "Unpin" : "Pin"}">${I.pin}</button>
      <button class="ibx-icon-btn" data-ibx-act="archive" title="${i.archived_at ? "Unarchive" : "Archive"} (e)" aria-label="${i.archived_at ? "Unarchive" : "Archive"}">${i.archived_at ? I.undo : I.archive}</button>
    </div>
  </div>
  ${detail}
</article>`;
}

function dataValue(v: unknown): string {
  if (v === null) return `<span class="muted">–</span>`;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return `<span class="num">${v.toLocaleString(undefined, { maximumFractionDigits: 6 })}</span>`;
  const s = String(v);
  if (/^https?:\/\/\S+$/.test(s)) return `<a href="${esc(s)}" target="_blank" rel="noopener noreferrer nofollow">${esc(s.replace(/^https?:\/\//, ""))}</a>`;
  return esc(s);
}

function detailHtml(i: InboxItem): string {
  const msg = i.message ? `<div class="ibx-md">${markdown(i.message)}</div>` : "";
  const data = i.data && Object.keys(i.data).length ? `<dl class="ibx-kv">${Object.entries(i.data).map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${dataValue(v)}</dd></div>`).join("")}</dl>` : "";
  const tags = i.tags.length ? `<div class="ibx-tags">${i.tags.map((t) => `<span class="ibx-tag">${esc(t)}</span>`).join("")}</div>` : "";
  const link = i.url
    ? `<a class="btn ink ibx-link" href="${esc(i.url)}" target="_blank" rel="noopener noreferrer nofollow">${esc(i.url_title || i.url.replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 48))}${I.out}</a>`
    : "";
  // Buttons that call MonoLog's API (Fix it on an auto-fix suggestion); the result shows next to them.
  const actions = i.actions
    .map((a) => `<button class="btn primary ibx-action" data-ibx-action="${esc(a.id)}" data-path="${esc(a.path)}">${esc(a.label)}</button>`)
    .join("");
  const actMsg = st.actionMsg.get(i.id);
  const meta = [
    `${i.label ? `${esc(i.label)} via ` : ""}${esc(i.source.name)}`,
    i.count > 1 ? `${plural(i.count, "time")} since ${esc(fullDate(i.created_at))}` : esc(fullDate(i.created_at)),
    i.count > 1 ? `latest ${esc(clock(i.last_at))}` : "",
  ].filter(Boolean);
  return `<div class="ibx-detail">
    ${msg}${data}${tags}
    <div class="ibx-foot">
      ${actions}${link}${actMsg ? `<span class="ibx-action-msg${actMsg.error ? " error" : ""}" role="status">${esc(actMsg.text)}</span>` : ""}
      <span class="ibx-meta">${meta.join('<span class="sep">·</span>')}</span>
      <span class="spacer"></span>
      <div class="ibx-acts">
        <button class="ibx-act" data-ibx-act="read">${i.read_at ? I.unread : I.check}<span>${i.read_at ? "Mark unread" : "Mark read"}</span></button>
        <button class="ibx-act${i.pinned ? " on" : ""}" data-ibx-act="pin">${I.pin}<span>${i.pinned ? "Unpin" : "Pin"}</span></button>
        <button class="ibx-act" data-ibx-act="archive">${i.archived_at ? I.undo : I.archive}<span>${i.archived_at ? "Unarchive" : "Archive"}</span></button>
      </div>
    </div>
  </div>`;
}

function groups(items: InboxItem[], now: number): string {
  let out = "";
  let cur = "";
  for (const i of items) {
    const label = dayLabel(i.last_at, now);
    if (label !== cur) {
      if (cur) out += `</div></section>`;
      out += `<section class="ibx-group"><h3 class="ibx-gh">${label}</h3><div class="ibx-list">`;
      cur = label;
    }
    out += itemHtml(i, now);
  }
  return cur ? `${out}</div></section>` : "";
}

/** The calm, empty states: no notifications yet, all caught up, or nothing matched. */
function emptyHtml(): string {
  const art = (kind: "quiet" | "done" | "none") => `<svg class="ibx-art" viewBox="0 0 200 140" fill="none" aria-hidden="true">
    <defs><linearGradient id="ibxg" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffffff"/><stop offset="1" stop-color="#f7f5f0"/></linearGradient></defs>
    <ellipse cx="100" cy="124" rx="70" ry="7" fill="#16233d" opacity=".05"/>
    <rect x="44" y="20" width="112" height="26" rx="9" fill="#fbfaf7" stroke="#e3dfd6"/>
    <rect x="34" y="36" width="132" height="34" rx="11" fill="#fbfaf7" stroke="#e3dfd6"/>
    <g transform="translate(22 54)">
      <rect width="156" height="58" rx="14" fill="url(#ibxg)" stroke="#e3dfd6"/>
      <rect x="14" y="15" width="28" height="28" rx="9" fill="${kind === "done" ? "#e3f1e8" : "#e4eaf8"}"/>
      ${kind === "done" ? '<path d="m21.5 29.5 5 5 9-10" stroke="#2f9e63" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>' : kind === "none" ? '<circle cx="27" cy="28" r="5.5" stroke="#2f5bd3" stroke-width="2"/><path d="m31 32 4 4" stroke="#2f5bd3" stroke-width="2" stroke-linecap="round"/>' : '<path d="M28 20.5a6 6 0 0 0-6 6v4l-1.6 2.6h15.2L34 30.5v-4a6 6 0 0 0-6-6Z" stroke="#2f5bd3" stroke-width="1.8" stroke-linejoin="round"/><path d="M26 36.2a2 2 0 0 0 4 0" stroke="#2f5bd3" stroke-width="1.8" stroke-linecap="round"/>'}
      <rect x="54" y="19" width="62" height="7" rx="3.5" fill="#16233d" opacity=".82"/>
      <rect x="54" y="32" width="86" height="6" rx="3" fill="#8a93a6" opacity=".35"/>
    </g>
    ${kind === "quiet" ? '<g fill="#2f5bd3"><path d="M162 14l1.6 4.2 4.2 1.6-4.2 1.6L162 25.6l-1.6-4.2-4.2-1.6 4.2-1.6Z" opacity=".55"/><path d="M34 98l1 2.6 2.6 1-2.6 1-1 2.6-1-2.6-2.6-1 2.6-1Z" opacity=".35"/></g>' : ""}
  </svg>`;
  const hasSources = st.sources.some((s) => !s.system);
  if (st.q.trim())
    return `<div class="ibx-empty">${art("none")}<h2>Nothing matches “${esc(st.q.trim())}”</h2><p>Search looks at titles, messages, tags and sources${st.filter !== "all" || st.source ? " in this view" : ""}.</p><button class="btn" data-ibx-clear>Clear search</button></div>`;
  if (st.filter === "unread" || (st.filter === "all" && st.counts && st.counts.archived + st.counts.pinned > 0))
    return `<div class="ibx-empty">${art("done")}<h2>All caught up</h2><p>Nothing new${st.source ? " from this source" : ""}. New notifications land here the moment they're sent.</p></div>`;
  if (st.filter === "critical") return `<div class="ibx-empty">${art("done")}<h2>No critical notifications</h2><p>Anything sent with <code>level: "critical"</code> shows up here, and pushes even during quiet hours.</p></div>`;
  if (st.filter === "pinned") return `<div class="ibx-empty">${art("quiet")}<h2>Nothing pinned</h2><p>Pin a notification to keep it at the top of Ping. Pinned ones are never cleaned up.</p></div>`;
  if (st.filter === "archived") return `<div class="ibx-empty">${art("quiet")}<h2>The archive is empty</h2><p>Archived notifications stay here for 30 days.</p></div>`;
  const curl = `curl ${notifyEndpoint(new URL(location.href))} \\\n  -H "Authorization: Bearer nt_…" \\\n  -d title="Hello from my server" -d level=success`;
  return `<div class="ibx-empty first">${art("quiet")}<h2>All quiet</h2>
    <p>${hasSources ? "Send a notification with one HTTP call and it lands here, then on your phone." : "Create a source for a site, app or script, and send notifications to it with one HTTP call. They land here and on your phone."}</p>
    <pre class="ibx-curl"><code>${esc(curl)}</code></pre>
    <button class="btn ink" data-ibx-settings>${hasSources ? "Tokens and snippets" : "Create a source"}</button></div>`;
}

function sideHtml(): string {
  const c = st.counts;
  const n = (k: InboxFilter) => (k === "all" || k === "unread" ? c?.unread : k === "critical" ? c?.critical : k === "pinned" ? c?.pinned : 0) ?? 0;
  const views = VIEWS.map(
    (v) =>
      `<button class="ibx-nav${st.filter === v.key ? " on" : ""}${v.key === "critical" && n(v.key) ? " hot" : ""}" data-ibx-filter="${v.key}" aria-pressed="${st.filter === v.key}">${v.icon}<span>${v.label}</span>${
        n(v.key) && v.key !== "archived" ? `<em>${n(v.key)}</em>` : ""
      }</button>`,
  ).join("");
  const srcs = st.sources
    .filter((s) => s.total || !s.system)
    .map(
      (s) =>
        `<button class="ibx-nav src${st.source === s.id ? " on" : ""}" data-ibx-source="${esc(s.id)}" aria-pressed="${st.source === s.id}">${avatar(s, "sm")}<span>${esc(s.name)}</span>${s.unread ? `<em>${s.unread}</em>` : ""}</button>`,
    )
    .join("");
  return `<nav class="ibx-views" aria-label="Views">${views}</nav>
    <div class="ibx-side-h"><span>Sources</span><button class="ibx-mini" data-ibx-settings title="Sources, tokens and push settings" aria-label="Notification settings">${I.gear}</button></div>
    <nav class="ibx-sources" aria-label="Sources">${srcs || `<p class="ibx-side-empty">No sources yet.</p>`}</nav>`;
}

function chipsHtml(): string {
  const c = st.counts;
  const views = VIEWS.map((v) => {
    const n = v.key === "unread" ? c?.unread : v.key === "critical" ? c?.critical : 0;
    return `<button class="ibx-chip${st.filter === v.key ? " on" : ""}${v.key === "critical" && n ? " hot" : ""}" data-ibx-filter="${v.key}" aria-pressed="${st.filter === v.key}">${v.label}${n ? `<em>${n}</em>` : ""}</button>`;
  }).join("");
  const srcs = st.sources
    .filter((s) => s.total || !s.system)
    .map((s) => `<button class="ibx-chip src${st.source === s.id ? " on" : ""}" data-ibx-source="${esc(s.id)}" aria-pressed="${st.source === s.id}">${avatar(s, "sm")}${esc(s.name)}</button>`)
    .join("");
  return `${views}${srcs ? `<span class="ibx-chip-sep" aria-hidden="true"></span>${srcs}` : ""}`;
}

function headSub(): string {
  const c = st.counts;
  if (!c) return "";
  const src = st.sources.find((s) => s.id === st.source);
  const bits = [src ? `From ${esc(src.name)}` : "", c.unread ? `${c.unread.toLocaleString()} unread` : "All read", c.critical ? `<span class="crit">${c.critical} critical</span>` : ""];
  return bits.filter(Boolean).join('<span class="sep">·</span>');
}

function feedHtml(): string {
  if (st.error) return `<div class="notice error">Couldn't load Ping (${esc(st.error)}).</div>`;
  if (!st.loaded) return `<div class="ibx-loading">${Array.from({ length: 6 }, (_, k) => `<div class="ibx-sk" style="--d:${k * 60}ms"><i></i><span><b></b><b></b></span></div>`).join("")}</div>`;
  const now = Date.now();
  const pinned = st.pinned.length ? `<section class="ibx-group pinned"><h3 class="ibx-gh">${I.pin}Pinned</h3><div class="ibx-list">${st.pinned.map((i) => itemHtml(i, now)).join("")}</div></section>` : "";
  if (!st.items.length && !st.pinned.length) return emptyHtml();
  const more = st.next ? `<div class="ibx-more"><button class="btn" data-ibx-more>Show older</button></div>` : st.items.length > 12 ? `<div class="ibx-end">That's everything${st.filter === "archived" ? "" : " · archived items are kept for 30 days"}</div>` : "";
  return `${pinned}${groups(st.items, now)}${more}`;
}

function shell(o: Opts): void {
  o.el.innerHTML = `<div class="ibx${o.mobile ? " m" : ""}">
    ${o.mobile ? "" : `<aside class="ibx-aside" id="ibx-aside">${sideHtml()}</aside>`}
    <section class="ibx-col">
      <header class="ibx-head">
        <div class="ibx-h"><h1 id="ibx-title">${VIEW_TITLE[st.filter]}</h1><span class="ibx-sub" id="ibx-sub">${headSub()}</span></div>
        <span class="spacer"></span>
        <label class="ibx-search">${I.search}<input type="search" id="ibx-q" placeholder="Search" value="${esc(st.q)}" autocomplete="off" spellcheck="false" aria-label="Search notifications">${o.mobile ? "" : `<kbd>/</kbd>`}</label>
        <button class="btn ibx-readall" data-ibx-readall ${st.counts?.unread ? "" : "disabled"} title="Mark all read">${I.checks}<span>Mark all read</span></button>
      </header>
      ${o.mobile ? `<div class="ibx-chips" id="ibx-chips" role="toolbar" aria-label="Filter">${chipsHtml()}</div>` : ""}
      <div class="ibx-feed card" id="ibx-feed" aria-live="polite">${feedHtml()}</div>
    </section>
  </div>
  <div class="ibx-toast" id="ibx-toast" role="status" hidden></div>`;
}

/** Repaints what depends on data, keeping the search box (and its focus) as it is. */
function paint(): void {
  const o = opts;
  if (!o || !o.current()) return;
  const feed = o.el.querySelector<HTMLElement>("#ibx-feed");
  if (!feed) return;
  feed.innerHTML = feedHtml();
  const side = o.el.querySelector<HTMLElement>("#ibx-aside");
  if (side) side.innerHTML = sideHtml();
  const chips = o.el.querySelector<HTMLElement>("#ibx-chips");
  if (chips) {
    const x = chips.scrollLeft;
    chips.innerHTML = chipsHtml();
    chips.scrollLeft = x;
  }
  o.el.querySelector("#ibx-title")!.textContent = VIEW_TITLE[st.filter];
  o.el.querySelector("#ibx-sub")!.innerHTML = headSub();
  const ra = o.el.querySelector<HTMLButtonElement>("[data-ibx-readall]");
  if (ra) ra.disabled = !st.counts?.unread;
  wireSwipes(feed);
}

// ---------- loading ----------

function absorb(v: InboxView, mode: "replace" | "append" | "poll"): void {
  const before = st.seen;
  if (mode === "append") st.items = [...st.items, ...v.items.filter((n) => !st.items.some((i) => i.id === n.id))];
  else {
    const kept = [...st.items, ...st.pinned];
    st.items = v.items;
    st.pinned = v.pinned;
    // An open item stays while it's being read, even if it no longer fits the view (read in Unread).
    if (mode === "poll")
      for (const id of st.open) {
        const old = kept.find((i) => i.id === id);
        if (old && !st.items.some((i) => i.id === id) && !st.pinned.some((i) => i.id === id)) st.items.push(old);
      }
    st.items.sort((a, b) => b.last_at - a.last_at || (a.id < b.id ? 1 : -1));
  }
  // New, or repeated since the last look: these glow for a moment.
  if (mode === "poll" && st.loaded)
    st.fresh = new Set([...st.items, ...st.pinned].filter((i) => before.get(i.id)?.split(":").slice(0, 2).join(":") !== `${i.last_at}:${i.count}`).map((i) => i.id));
  else if (mode === "replace") st.fresh = new Set();
  st.next = v.next;
  st.counts = v.counts;
  st.sources = v.sources;
  st.seen = new Map([...st.items, ...st.pinned].map((i) => [i.id, sig(i)]));
  st.loaded = true;
  st.error = null;
  setInboxBadge(v.counts);
}

async function load(mode: "replace" | "append" | "poll" = "replace"): Promise<void> {
  const g = ++gen;
  try {
    const extra: Record<string, string> =
      mode === "append" && st.next ? { before: st.next, limit: String(PAGE) } : { limit: String(Math.min(200, Math.max(PAGE, mode === "poll" ? st.items.length : PAGE))) };
    const v = await call<InboxView>("GET", `inbox?${query(extra)}`);
    if (g !== gen || !opts?.current()) return;
    const sigBefore = [...st.items, ...st.pinned].map(sig).join("|") + JSON.stringify(st.counts);
    absorb(v, mode);
    if (mode === "poll" && sigBefore === [...st.items, ...st.pinned].map(sig).join("|") + JSON.stringify(st.counts)) return refreshTimes();
    paint();
  } catch (err) {
    if (g !== gen || !opts?.current()) return;
    if (mode === "poll") return; // keep what's on screen; the next tick tries again
    st.error = (err as Error).message;
    paint();
  }
}

const poll = () => load("poll");

/** Relative times move on even when nothing else changed. */
function refreshTimes(): void {
  const now = Date.now();
  for (const t of opts?.el.querySelectorAll<HTMLTimeElement>(".ibx-side-meta time") ?? []) t.textContent = when(Date.parse(t.dateTime), now);
}

// ---------- actions ----------

const find = (id: string) => st.items.find((i) => i.id === id) ?? st.pinned.find((i) => i.id === id);

let toastTimer: ReturnType<typeof setTimeout> | null = null;
function toast(text: string, undo?: () => void): void {
  const t = opts?.el.querySelector<HTMLElement>("#ibx-toast");
  if (!t) return;
  t.innerHTML = `<span>${esc(text)}</span>${undo ? `<button data-ibx-undo>${I.undo}Undo</button>` : ""}`;
  t.hidden = false;
  t.classList.remove("show");
  void t.offsetWidth;
  t.classList.add("show");
  t.querySelector("[data-ibx-undo]")?.addEventListener("click", () => {
    t.hidden = true;
    undo?.();
  });
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (t.hidden = true), 5000);
}

const ACTION_ERRORS: Record<string, string> = {
  github_token_missing: "The GitHub token isn't set up on MonoLog yet.",
  no_repo: "The site has no GitHub repository set.",
  autofix_off: "Auto-fix is off for this site.",
  in_progress: "A fix is already in progress.",
  already_fixed: "A pull request is already open for it.",
  not_allowed: "Only the site's owners and admins can do this.",
  read_only: "Only the site's owners and admins can do this.",
};

/** An action button (Fix it): POST its path on this host, then show what happened. */
async function runAction(id: string, btn: HTMLButtonElement): Promise<void> {
  const it = find(id);
  const path = btn.dataset.path ?? "";
  if (!it || !path.startsWith("/api/")) return;
  btn.disabled = true;
  btn.textContent = btn.dataset.ibxAction === "ignore" ? "Ignoring…" : "Asking…";
  let msg: { text: string; error: boolean };
  try {
    const res = await fetch(path, { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: "{}" });
    const out = (await res.json().catch(() => ({}))) as { error?: string; message?: string; attempt?: { repo?: string | null } };
    const ignore = btn.dataset.ibxAction === "ignore";
    msg = res.ok
      ? ignore
        ? { text: "Ignored. It won't count towards error rates or alerts any more.", error: false }
        : { text: `Sent to ${out.attempt?.repo ?? "GitHub"}. Claude is on it; you'll get a notification when there's a pull request.`, error: false }
      : { text: out.message ?? ACTION_ERRORS[out.error ?? ""] ?? `Couldn't do that (${out.error ?? `HTTP ${res.status}`}).`, error: true };
    // The suggestion is answered either way once GitHub has been asked: its button goes.
    if (res.ok || out.error === "dispatch_failed" || out.error === "in_progress" || out.error === "already_fixed") it.actions = [];
  } catch (err) {
    msg = { text: `Couldn't do that (${(err as Error).message}).`, error: true };
  }
  st.actionMsg.set(id, msg);
  if (!it.read_at) void update(id, { read: true }, true);
  else paint();
}

/** Applies a change locally at once, then on the server; puts it back if the server refuses. */
async function update(id: string, patch: { read?: boolean; pinned?: boolean; archived?: boolean }, quiet = false): Promise<void> {
  const it = find(id);
  if (!it) return;
  const prev = { ...it };
  const now = Date.now();
  if (patch.read !== undefined) it.read_at = patch.read ? (it.read_at ?? now) : null;
  if (patch.pinned !== undefined) it.pinned = patch.pinned;
  if (patch.archived !== undefined) {
    it.archived_at = patch.archived ? now : null;
    if (patch.archived) it.read_at ??= now;
  }
  // Leaves the current view when it no longer belongs there.
  const leaves =
    (patch.archived !== undefined && (st.filter === "archived") !== !!it.archived_at) ||
    (st.filter === "pinned" && !it.pinned) ||
    (patch.pinned !== undefined && st.filter === "all");
  const el = opts?.el.querySelector<HTMLElement>(`[data-ibx-id="${CSS.escape(id)}"]`);
  const commitLocal = () => {
    if (patch.pinned !== undefined && st.filter === "all") {
      st.pinned = it.pinned ? [it, ...st.pinned.filter((x) => x.id !== id)] : st.pinned.filter((x) => x.id !== id);
      st.items = it.pinned ? st.items.filter((x) => x.id !== id) : [...st.items.filter((x) => x.id !== id), it].sort((a, b) => b.last_at - a.last_at || (a.id < b.id ? 1 : -1));
    } else if (leaves) {
      st.items = st.items.filter((x) => x.id !== id);
      st.pinned = st.pinned.filter((x) => x.id !== id);
    }
    adjustCounts(prev, it);
    st.seen.set(id, sig(it));
    paint();
  };
  if (leaves && el && patch.archived !== undefined) {
    el.style.height = `${el.offsetHeight}px`;
    el.classList.add("leaving");
    void el.offsetHeight;
    el.style.height = "0px";
    setTimeout(commitLocal, 220);
  } else commitLocal();
  try {
    await call("PATCH", `inbox/${encodeURIComponent(id)}`, patch);
  } catch (err) {
    Object.assign(it, prev);
    toast(`Couldn't update it (${(err as Error).message}).`);
    void load("poll");
    return;
  }
  if (quiet) return;
  if (patch.archived) toast("Archived", () => void update(id, { archived: false }, true).then(() => load("poll")));
  else if (patch.archived === false) toast("Moved back to Ping");
}

function adjustCounts(a: InboxItem, b: InboxItem): void {
  const c = st.counts;
  if (!c) return;
  const live = (i: InboxItem) => !i.archived_at;
  const u = (i: InboxItem) => (live(i) && !i.read_at ? 1 : 0);
  const cr = (i: InboxItem) => (u(i) && i.level === "critical" ? 1 : 0);
  c.unread += u(b) - u(a);
  c.critical += cr(b) - cr(a);
  c.pinned += (live(b) && b.pinned ? 1 : 0) - (live(a) && a.pinned ? 1 : 0);
  c.archived += (b.archived_at ? 1 : 0) - (a.archived_at ? 1 : 0);
  const s = st.sources.find((x) => x.id === b.source.id);
  if (s) s.unread += u(b) - u(a);
  setInboxBadge(c);
}

function toggle(id: string): void {
  const it = find(id);
  if (!it) return;
  st.focus = id;
  if (st.open.has(id)) st.open.delete(id);
  else {
    st.open.add(id);
    if (!it.read_at) return void update(id, { read: true }, true);
  }
  paint();
}

async function readAll(): Promise<void> {
  const unread = [...st.items, ...st.pinned].filter((i) => !i.read_at && !i.archived_at);
  const now = Date.now();
  for (const i of unread) i.read_at = now;
  if (st.filter === "unread") st.items = [];
  paint();
  try {
    const r = await call<{ updated: number; counts: InboxCounts }>("POST", "inbox/read-all", st.source ? { source: st.source } : {});
    st.counts = r.counts;
    toast(r.updated ? `Marked ${plural(r.updated, "notification")} read` : "Everything was already read");
  } catch (err) {
    toast(`Couldn't mark them read (${(err as Error).message}).`);
  }
  void load("poll");
}

function setFilter(f: InboxFilter): void {
  if (st.filter === f) return;
  st.filter = f;
  reset();
}
function setSource(id: string): void {
  st.source = st.source === id ? "" : id;
  reset();
}
function reset(): void {
  st.loaded = false;
  st.items = [];
  st.pinned = [];
  st.next = null;
  st.fresh = new Set();
  paint();
  void load("replace");
}

// ---------- swipe (touch) ----------

function wireSwipes(feed: HTMLElement): void {
  if (!opts?.mobile) return;
  for (const row of feed.querySelectorAll<HTMLElement>(".ibx-row")) {
    const item = row.closest<HTMLElement>(".ibx-item")!;
    let x0 = 0;
    let y0 = 0;
    let dx = 0;
    let mode: "idle" | "swipe" | "scroll" = "idle";
    row.addEventListener(
      "touchstart",
      (e) => {
        x0 = e.touches[0]!.clientX;
        y0 = e.touches[0]!.clientY;
        dx = 0;
        mode = "idle";
        row.style.transition = "none";
      },
      { passive: true },
    );
    row.addEventListener(
      "touchmove",
      (e) => {
        const t = e.touches[0]!;
        const mx = t.clientX - x0;
        const my = t.clientY - y0;
        if (mode === "idle") {
          if (Math.abs(mx) > 10 && Math.abs(mx) > Math.abs(my) * 1.4) mode = "swipe";
          else if (Math.abs(my) > 10) mode = "scroll";
        }
        if (mode !== "swipe") return;
        e.preventDefault();
        dx = mx;
        const damp = Math.abs(dx) > 120 ? Math.sign(dx) * (120 + (Math.abs(dx) - 120) * 0.35) : dx;
        row.style.transform = `translateX(${damp}px)`;
        item.dataset.swipe = dx < 0 ? "left" : "right";
        item.classList.toggle("armed", Math.abs(dx) > 88);
      },
      { passive: false },
    );
    const end = () => {
      row.style.transition = "";
      const id = item.dataset.ibxId!;
      const it = find(id);
      if (mode === "swipe" && it && Math.abs(dx) > 88) {
        if (dx < 0) {
          row.style.transform = "translateX(-110%)";
          void update(id, { archived: !it.archived_at });
        } else {
          row.style.transform = "";
          void update(id, { read: !it.read_at }, true);
        }
      } else row.style.transform = "";
      item.classList.remove("armed");
      if (mode !== "swipe" || Math.abs(dx) <= 88 || dx > 0) setTimeout(() => delete item.dataset.swipe, 300);
      if (mode === "swipe") row.dataset.swiped = "1";
      mode = "idle";
    };
    row.addEventListener("touchend", end);
    row.addEventListener("touchcancel", end);
  }
}

// ---------- events (wired once) ----------

let wired = false;
function wire(): void {
  if (wired) return;
  wired = true;
  document.addEventListener("click", (ev) => {
    const o = opts;
    if (!o?.current()) return;
    const t = ev.target as HTMLElement;
    if (!o.el.contains(t)) return;
    const action = t.closest<HTMLButtonElement>("[data-ibx-action]");
    if (action) {
      ev.stopPropagation();
      const id = action.closest<HTMLElement>("[data-ibx-id]")?.dataset.ibxId;
      if (id) void runAction(id, action);
      return;
    }
    const act = t.closest<HTMLElement>("[data-ibx-act]");
    if (act) {
      ev.stopPropagation();
      const id = act.closest<HTMLElement>("[data-ibx-id]")?.dataset.ibxId;
      const it = id ? find(id) : null;
      if (!it || !id) return;
      const a = act.dataset.ibxAct;
      if (a === "read") void update(id, { read: !it.read_at }, true);
      else if (a === "pin") void update(id, { pinned: !it.pinned }, true);
      else if (a === "archive") void update(id, { archived: !it.archived_at });
      return;
    }
    if (t.closest("a")) return; // links in the message open normally
    const row = t.closest<HTMLElement>("[data-ibx-toggle]");
    if (row) {
      if (row.dataset.swiped) return void delete row.dataset.swiped;
      return toggle(row.closest<HTMLElement>("[data-ibx-id]")!.dataset.ibxId!);
    }
    const f = t.closest<HTMLElement>("[data-ibx-filter]");
    if (f) return setFilter(f.dataset.ibxFilter as InboxFilter);
    const s = t.closest<HTMLElement>("[data-ibx-source]");
    if (s) return setSource(s.dataset.ibxSource!);
    if (t.closest("[data-ibx-readall]")) return void readAll();
    if (t.closest("[data-ibx-more]")) {
      (t.closest("[data-ibx-more]") as HTMLButtonElement).disabled = true;
      return void load("append");
    }
    if (t.closest("[data-ibx-settings]")) return o.onSettings();
    if (t.closest("[data-ibx-clear]")) {
      st.q = "";
      const q = o.el.querySelector<HTMLInputElement>("#ibx-q");
      if (q) q.value = "";
      return reset();
    }
  });
  let qTimer: ReturnType<typeof setTimeout> | null = null;
  document.addEventListener("input", (ev) => {
    const t = ev.target as HTMLElement;
    if (t.id !== "ibx-q" || !opts?.current()) return;
    if (qTimer) clearTimeout(qTimer);
    qTimer = setTimeout(() => {
      st.q = (t as HTMLInputElement).value;
      reset();
    }, 220);
  });
  document.addEventListener("keydown", (ev) => {
    const o = opts;
    if (!o?.current() || ev.metaKey || ev.ctrlKey || ev.altKey) return;
    const t = ev.target as HTMLElement;
    const typing = t.matches("input, textarea, select, [contenteditable]");
    if (typing) {
      if (ev.key === "Escape" && t.id === "ibx-q") (t as HTMLInputElement).blur();
      return;
    }
    const all = [...st.pinned, ...st.items];
    const idx = all.findIndex((i) => i.id === st.focus);
    const move = (d: number) => {
      const next = all[Math.max(0, Math.min(all.length - 1, (idx < 0 ? (d > 0 ? -1 : 0) : idx) + d))];
      if (!next) return;
      st.focus = next.id;
      paint();
      o.el.querySelector(`[data-ibx-id="${CSS.escape(next.id)}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    };
    const cur = idx >= 0 ? all[idx] : null;
    switch (ev.key) {
      case "j":
      case "ArrowDown":
        if (ev.key === "ArrowDown" && !st.focus) return;
        ev.preventDefault();
        return move(1);
      case "k":
      case "ArrowUp":
        if (ev.key === "ArrowUp" && !st.focus) return;
        ev.preventDefault();
        return move(-1);
      case "Enter":
      case "o":
        if (cur && (ev.key === "o" || t === document.body)) {
          ev.preventDefault();
          toggle(cur.id);
        }
        return;
      case "Escape":
        if (st.open.size) {
          st.open.clear();
          paint();
        }
        return;
      case "e":
        if (cur) void update(cur.id, { archived: !cur.archived_at });
        return;
      case "p":
        if (cur) void update(cur.id, { pinned: !cur.pinned }, true);
        return;
      case "u":
        if (cur) void update(cur.id, { read: !cur.read_at }, true);
        return;
      case "/":
        ev.preventDefault();
        o.el.querySelector<HTMLInputElement>("#ibx-q")?.focus();
        return;
    }
  });
  document.addEventListener("keydown", (ev) => {
    const t = ev.target as HTMLElement;
    if ((ev.key === "Enter" || ev.key === " ") && t.matches("[data-ibx-toggle]") && opts?.current()) {
      ev.preventDefault();
      toggle(t.closest<HTMLElement>("[data-ibx-id]")!.dataset.ibxId!);
    }
  });
}

// ---------- entry point ----------

export async function renderInbox(o: Opts): Promise<void> {
  opts = o;
  wire();
  if (o.openId) {
    st.open.add(o.openId);
    st.focus = o.openId;
    // A tapped push opens on the full inbox, so the item is there to be found.
    if (st.filter !== "all" || st.source || st.q) Object.assign(st, { filter: "all", source: "", q: "", loaded: false });
  }
  shell(o);
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = setInterval(() => {
    if (!o.current()) {
      clearInterval(pollTimer!);
      pollTimer = null;
      return;
    }
    if (!document.hidden) void poll();
  }, POLL_MS);
  await load(st.loaded ? "poll" : "replace");
  if (st.loaded && !o.current()) return;
  paint();
  if (o.openId && o.current()) {
    const it = find(o.openId);
    if (it && !it.read_at) void update(it.id, { read: true }, true);
    requestAnimationFrame(() => o.el.querySelector(`[data-ibx-id="${CSS.escape(o.openId!)}"]`)?.scrollIntoView({ block: "center" }));
  }
}

/** Counts last seen by the badge (for app.ts). */
export const inboxCounts = () => lastCounts;
